import { useState, useEffect } from 'react';
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import { ExternalLink, X } from 'lucide-react';
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

export interface ViewerLink {
  url: string;
  label: string;
}

interface ImageViewerProps {
  images: string[];
  initialIndex: number;
  onClose: () => void;
  relatedLinks?: (ViewerLink | undefined)[];
}

export function ImageViewer({ images, initialIndex, onClose, relatedLinks }: ImageViewerProps) {
  const [index, setIndex] = useState(initialIndex);

  // Manage Body/HTML background to prevent white bleeds and ensure clean up
  useEffect(() => {
    // Save current background
    const originalHtmlBg = document.documentElement.style.background;
    const originalBodyBg = document.body.style.background;

    // Force black background on root elements to prevent white overscroll
    // styling while lightbox is open - REQUESTED BEHAVIOR
    document.documentElement.style.background = '#000000';
    document.body.style.background = '#000000';

    return () => {
      // Restore original background
      document.documentElement.style.background = originalHtmlBg;
      document.body.style.background = originalBodyBg;
    };
  }, []);

  const slides = images.map(src => ({ src }));

  return (
    <Lightbox
      open={true}
      close={onClose}
      index={index}
      slides={slides}
      plugins={[Zoom, Thumbnails]}
      on={{
        view: ({ index: newIndex }) => setIndex(newIndex),
      }}
      render={{
        buttonClose: () => (
          <div className="fixed top-0 left-0 right-0 z-[9999] bg-black/80 backdrop-blur-md border-b border-white/10 pointer-events-auto flex items-center justify-between p-4 sm:px-6 px-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 transition-all active:scale-95"
                onClick={onClose}
                title="Close"
                aria-label="Close"
              >
                <X size={24} className="text-white" />
              </button>
              <div className="flex flex-col">
                <span className="text-white text-sm font-bold tracking-tight">Photo</span>
                <span className="text-white/50 text-[10px] uppercase font-black tracking-widest leading-none">
                  {index + 1} / {images.length}
                </span>
              </div>
            </div>

            <div className="flex items-center">
              {relatedLinks?.[index] && (
                <a
                  href={relatedLinks[index]!.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-xl hover:bg-white/10 text-blue-400 hover:text-blue-300 transition-all flex items-center gap-2 px-3 tracking-wide text-xs font-bold uppercase pointer-events-auto"
                  style={{ textDecoration: 'none' }}
                >
                  <span className="hidden sm:inline">{relatedLinks[index]!.label}</span>
                  <ExternalLink size={18} />
                </a>
              )}
            </div>
          </div>
        )
      }}
      animation={{ fade: 250 }}
      carousel={{ padding: 0, spacing: 0 }}
      zoom={{
        scrollToZoom: true,
        maxZoomPixelRatio: 5,
      }}
      styles={{
        container: { backgroundColor: "rgba(0, 0, 0, .95)" },
        root: { "--yarl__color_backdrop": "rgba(0, 0, 0, .95)" } as any,
        slide: { paddingTop: "80px" }
      }}
      thumbnails={{
        position: "bottom",
        width: 80,
        height: 60,
        border: 0,
        borderRadius: 4,
        padding: 0,
        gap: 10,
        showToggle: false,
        vignette: false,
      }}
      controller={{ closeOnBackdropClick: false }}
    />
  );
}
